import { FullscreenOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Alert, Empty, Space, Typography, Image, Button } from 'antd';
import React from 'react';

import useAPIQuery from '../api/useAPIQuery';
import { CandidateDetail, CandidateDisabilityCollection } from '../types';

export interface CandidateFileProps {
  file?: CandidateDetail['attachment'] | CandidateDisabilityCollection['report'];
  has_file?: boolean;
  tenant: string;
  enableAntivirusVerification?: boolean;
  antivirusFileStatus?:
    | CandidateDetail['antivirus_file_status']
    | CandidateDisabilityCollection['antivirus_file_status'];
  fileHeader?: React.ReactNode;
  emptyFileReactComponent?: React.ReactNode;
  setIsFullScreenVisible: (value: React.SetStateAction<boolean>) => void;
  setFullScreenFile: (
    value: React.SetStateAction<
      CandidateDetail['attachment'] | CandidateDisabilityCollection['report']
    >
  ) => void;
  setFullScreenAccessToken: (value: React.SetStateAction<string>) => void;
}

const CandidateFile = ({
  file,
  has_file,
  tenant,
  enableAntivirusVerification,
  antivirusFileStatus,
  fileHeader = <></>,
  emptyFileReactComponent = <Empty />,
  setIsFullScreenVisible,
  setFullScreenFile,
  setFullScreenAccessToken,
}: CandidateFileProps) => {
  const requestFileAccess = useAPIQuery(`/file-access/request-file-access/${file?.name}/`, {
    enabled:
      !!file &&
      [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
      ].includes(file.mimetype || ''),
  });

  return (
    <>
      {enableAntivirusVerification && antivirusFileStatus === 'WITH_VIRUS' && (
        <>
          {fileHeader}
          <Alert
            message="Aviso: O anexo enviado contém um arquivo identificado como vírus e, por isso, não será exibido."
            type="error"
            showIcon
          />
        </>
      )}
      {enableAntivirusVerification && antivirusFileStatus === 'INVALID_FILE' && (
        <>
          {fileHeader}
          <Alert
            message="Aviso: O anexo enviado contém um formato inválido ou corrompido, por isso, não será exibido."
            type="error"
            showIcon
          />
        </>
      )}
      {enableAntivirusVerification && antivirusFileStatus === 'VERIFICATION_FAILED' && (
        <>
          {fileHeader}
          <Alert
            message="Aviso: O anexo enviado não pode ser verificado pelo sistema de segurança, contate o suporte."
            type="error"
            showIcon
          />
        </>
      )}
      {enableAntivirusVerification && antivirusFileStatus === 'NOT_VERIFIED' && has_file && (
        <>
          {fileHeader}
          <Alert
            message="Aviso: Esse anexo ainda não foi verificado pelo sistema de segurança e, por isso, não será exibido."
            type="warning"
            showIcon
          />
        </>
      )}
      {file?.name && file?.url && (
        <>
          {fileHeader}
          <Space>
            <Typography.Link
              data-testid={`attachment-link-${file.name}`}
              href={
                file.url.startsWith('http') ? `${file.url}` : `${window.location.origin}${file.url}`
              }
              target="_blank"
            >
              <PaperClipOutlined /> {file.name}
            </Typography.Link>
            <Button
              icon={<FullscreenOutlined />}
              disabled={
                enableAntivirusVerification &&
                !['NO_VIRUS', 'BEFORE_AV'].includes(antivirusFileStatus || '')
              }
              onClick={() => {
                setIsFullScreenVisible(true);
                setFullScreenFile(file);
                setFullScreenAccessToken(requestFileAccess.data?.access_token || '');
              }}
            />
          </Space>
        </>
      )}
      <br />
      <br />
      {file?.mimetype === 'application/pdf' && (
        <iframe
          data-testid={`attachment-iframe-${file.name}`}
          height={785}
          width="100%"
          src={
            file.url.startsWith('http')
              ? `${file.url}`
              : `${window.location.origin}/${tenant}/download/${file.name}?type=iframe`
          }
          style={{ overflow: 'hidden', flex: 1 }}
        />
      )}
      {['image/png', 'image/jpeg', 'image/webp'].includes(file?.mimetype || '') && (
        <Image
          style={{ maxHeight: 785 }}
          src={`${window.location.origin}/${tenant}/download/${file?.name}?type=iframe`}
        />
      )}
      {enableAntivirusVerification
        ? !file && !has_file && antivirusFileStatus !== 'WITH_VIRUS' && emptyFileReactComponent
        : !file && emptyFileReactComponent}
      {file &&
        requestFileAccess.data?.access_token &&
        [
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/msword',
        ].includes(file?.mimetype || '') && (
          <iframe
            width="100%"
            height={785}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${window.location.origin}/${tenant}/download-officeapps-file/${file.name}?access_token=${requestFileAccess.data.access_token}`}
            frameBorder="0"
          />
        )}
    </>
  );
};

export default CandidateFile;
